if (!window.BUNDLE_COLOR_MAP) {
  window.BUNDLE_COLOR_MAP = {
    'black': '#0a0a0a',
    'white': '#ffffff',
    'electric-blue': '#0057ff',
    'ink-blue': '#1b2a4a',
    'rain-blue': '#5a8fc0',
    'ice-blue': '#cfe8f3',
    'winter-berry': '#7b2942',
    'winterberry': '#7b2942',
    'bold-pink': '#e0457b',
    'floral-pink': '#f3b6c5',
    'light-pink': '#f7c9d6',
    'pink': '#f4a6c1',
    'hibiscus-red': '#b3273c',
    'raspberry': '#9c2b4e',
    'graphite-grey': '#4a4a4a',
    'graphite-gray': '#4a4a4a',
    'slate-grey': '#6b7280',
    'slate-gray': '#6b7280',
    'mushroom-grey': '#a89f96',
    'mushroom-gray': '#a89f96',
    'dark-coffee': '#4b3621',
    'tan-brown': '#a97a53',
    'light-yellow': '#f5e6a8',
    'pine-green': '#1f4d3a'
  };
}

class BundleItemSelector extends HTMLElement {
  constructor() {
    super();
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
  }

  connectedCallback() {
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.addBtn = this.querySelector('[data-bundle-add-btn]');
    this.totalPriceEl = this.querySelector('[data-bundle-total-price]');
    this.totalCompareEl = this.querySelector('[data-bundle-total-compare]');
    this.errorEl = this.querySelector('[data-bundle-error]');

    this.items = Array.from(this.querySelectorAll('[data-bundle-item]')).map((el) => this.initItem(el));

    if (this.addBtn) this.addBtn.addEventListener('click', () => this.addSetToCart());
    this.updateTotals();
  }

  /* Whole amounts render without decimals, matching the PDP, cards and cart
     (money_without_trailing_zeros). The theme's formatPrice always pads to .00,
     so it is only used when there actually are cents. */
  money(cents) {
    const whole = cents % 100 === 0;
    if (typeof formatPrice === 'function' && !whole) return formatPrice(cents, this.moneyFormat);
    const amount = whole ? String(cents / 100) : (cents / 100).toFixed(2);
    const format = this.moneyFormat || this.dataset.moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  initItem(el) {
    const raw = el.querySelector('[data-bundle-item-data]');
    const data = JSON.parse(raw.textContent);
    const swatches = Array.from(el.querySelectorAll('[data-bundle-swatch]'));
    const sizes = Array.from(el.querySelectorAll('[data-bundle-size]'));

    swatches.forEach((btn) => {
      if (!btn.style.getPropertyValue('--swatch-bg')) {
        const hex = window.BUNDLE_COLOR_MAP[btn.dataset.colorName];
        if (hex) btn.style.setProperty('--swatch-bg', hex);
      }
    });

    const item = {
      el,
      variants: data.variants,
      colorIndex: data.colorOptionIndex,
      sizeIndex: data.sizeOptionIndex,
      swatchEls: swatches,
      sizeEls: sizes,
      selectedColor: swatches.length ? swatches[0].dataset.value : null,
      selectedSize: sizes.length ? sizes[0].dataset.value : null,
      priceEl: el.querySelector('[data-bundle-item-price]'),
      compareEl: el.querySelector('[data-bundle-item-compare]'),
      imageEl: el.querySelector('[data-bundle-item-image]')
    };

    swatches.forEach((btn) => {
      btn.addEventListener('click', () => {
        swatches.forEach((b) => {
          b.classList.remove('is-selected');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-pressed', 'true');
        item.selectedColor = btn.dataset.value;
        const img = btn.dataset.image;
        if (img && item.imageEl) item.imageEl.src = img;
        this.syncItem(item);
        this.updateTotals();
      });
    });

    sizes.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-disabled')) return;
        sizes.forEach((b) => {
          b.classList.remove('is-selected');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-pressed', 'true');
        item.selectedSize = btn.dataset.value;
        this.syncItem(item);
        this.updateTotals();
      });
    });

    this.syncItem(item);
    return item;
  }

  findVariant(item) {
    return item.variants.find((v) => {
      const opts = v.options || [];
      const colorOk = item.colorIndex == null || opts[item.colorIndex] === item.selectedColor;
      const sizeOk = item.sizeIndex == null || opts[item.sizeIndex] === item.selectedSize;
      return colorOk && sizeOk;
    });
  }

  syncItem(item) {
    if (item.sizeEls.length && item.colorIndex != null) {
      item.sizeEls.forEach((btn) => {
        const match = item.variants.find(
          (v) => v.options[item.colorIndex] === item.selectedColor && v.options[item.sizeIndex] === btn.dataset.value
        );
        btn.classList.toggle('is-disabled', !match || !match.available);
      });
    }

    const variant = this.findVariant(item);
    item.selectedVariant = variant || null;
    if (!variant) return;

    if (item.priceEl) item.priceEl.textContent = this.money(Math.round(Number(variant.price)));
    if (item.compareEl) {
      const hasCompare = variant.compareAtPrice && Number(variant.compareAtPrice) > Number(variant.price);
      item.compareEl.style.display = hasCompare ? '' : 'none';
      if (hasCompare) item.compareEl.textContent = this.money(Math.round(Number(variant.compareAtPrice)));
    }
  }

  updateTotals() {
    let total = 0;
    let compareTotal = 0;
    let allAvailable = true;

    this.items.forEach((item) => {
      const variant = item.selectedVariant;
      if (!variant) {
        allAvailable = false;
        return;
      }
      total += Number(variant.price);
      const hasCompare = variant.compareAtPrice && Number(variant.compareAtPrice) > Number(variant.price);
      compareTotal += hasCompare ? Number(variant.compareAtPrice) : Number(variant.price);
      if (!variant.available) allAvailable = false;
    });

    if (this.totalPriceEl) this.totalPriceEl.textContent = this.money(Math.round(total));
    if (this.totalCompareEl) {
      const showCompare = compareTotal > total;
      this.totalCompareEl.style.display = showCompare ? '' : 'none';
      if (showCompare) this.totalCompareEl.textContent = this.money(Math.round(compareTotal));
    }

    if (this.addBtn) this.addBtn.toggleAttribute('disabled', !allAvailable);

    /* Bold: the CTA moved to its own block, so the state it needs is published
       rather than written straight into a button this element owns. */
    this.dispatchEvent(
      new CustomEvent('bundle:change', {
        bubbles: true,
        detail: {
          allAvailable,
          total,
          compareTotal,
          count: this.items.length,
          selection: this.selection
        }
      })
    );
  }

  /* Bold: the selected variants, in the shape /cart/add.js expects. Empty until
     every item has an available variant chosen, so a caller cannot half-add a set. */
  get selection() {
    const chosen = this.items.map(item => item.selectedVariant).filter(Boolean);
    if (chosen.length !== this.items.length) return [];
    return chosen.map(v => ({ id: v.id, quantity: 1 }));
  }

  /* Bold: accepts the button that triggered it, so the CTA can live in another
     block. Falls back to this.addBtn for any legacy markup that still has one. */
  addSetToCart(trigger) {
    const btn = trigger || this.addBtn;
    const items = this.selection;

    if (!items.length) return;

    if (btn) {
      btn.setAttribute('disabled', '');
      btn.classList.add('is-loading');
    }
    if (this.errorEl) this.errorEl.hidden = true;

    const config = typeof fetchConfig === 'function' ? fetchConfig('json') : {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    };

    const body = { items };
    if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
      body.sections = this.cart.getSectionsToRender().map((s) => s.section);
      body.sections_url = window.location.pathname;
    }
    config.body = JSON.stringify(body);

    fetch((typeof routes !== 'undefined' && routes.cart_add_url) || '/cart/add.js', config)
      .then((r) => r.json())
      .then((response) => {
        if (response.errors) {
          const message = typeof response.errors === 'string' ? response.errors : Object.values(response.errors).join(' ');
          this.dispatchEvent(new CustomEvent('bundle:error', { bubbles: true, detail: { message } }));
          if (this.errorEl) {
            this.errorEl.textContent = message;
            this.errorEl.hidden = false;
          }
          return;
        }
        if (this.cart && typeof this.cart.renderContents === 'function') {
          this.cart.renderContents(response);
        } else {
          window.location.href = '/cart';
        }
        if (typeof updateCartCounters === 'function') updateCartCounters();
      })
      .catch((err) => {
        console.error(err);
        this.dispatchEvent(
          new CustomEvent('bundle:error', {
            bubbles: true,
            detail: { message: 'Something went wrong adding the set to your cart.' }
          })
        );
        if (this.errorEl) {
          this.errorEl.textContent = 'Something went wrong adding the set to your cart.';
          this.errorEl.hidden = false;
        }
      })
      .finally(() => {
        if (btn) {
          btn.removeAttribute('disabled');
          btn.classList.remove('is-loading');
        }
      });
  }
}

if (!customElements.get('bundle-item-selector')) {
  customElements.define('bundle-item-selector', BundleItemSelector);
}
